import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { PageHeaderCard } from "@/components/layout/PageHeaderCard";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Shield, ShieldOff, Users, UserX } from "lucide-react";

type UserRow = {
  id: string;
  name: string | null;
  email: string | null;
  empresa_id: number | null;
  nivel: number | null;
  created_at: string;
  empresas?: {
    razao_social: string | null;
    nome_fantasia: string | null;
  } | null;
};

export default function AdminUsers() {
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ["admin-users-management", searchTerm],
    queryFn: async () => {
      let query = supabase
        .from("users")
        .select("id, name, email, empresa_id, nivel, created_at, empresas(razao_social, nome_fantasia)")
        .order("created_at", { ascending: false });

      if (searchTerm.trim()) {
        query = query.or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as UserRow[];
    },
  });

  const adminRolesQuery = useQuery({
    queryKey: ["admin-users-management-roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "admin");

      if (error) throw error;
      return new Set((data || []).map((row) => row.user_id));
    },
  });

  const isLoading = usersQuery.isLoading || adminRolesQuery.isLoading;
  const users = usersQuery.data || [];
  const adminIds = adminRolesQuery.data || new Set<string>();

  const totals = useMemo(() => {
    const totalUsers = users.length;
    const totalAdmins = users.filter((user) => adminIds.has(user.id)).length;
    const semEmpresa = users.filter((user) => user.empresa_id == null).length;

    return { totalUsers, totalAdmins, semEmpresa };
  }, [users, adminIds]);

  const toggleAdminRole = async (user: UserRow, makeAdmin: boolean) => {
    setUpdatingUserId(user.id);

    try {
      if (makeAdmin) {
        const { error } = await supabase.from("user_roles").insert({
          user_id: user.id,
          role: "admin",
        });

        if (error) throw error;
        toast.success("Permissão de administrador concedida");
      } else {
        const { error } = await supabase
          .from("user_roles")
          .delete()
          .eq("user_id", user.id)
          .eq("role", "admin");

        if (error) throw error;
        toast.success("Permissão de administrador removida");
      }

      await Promise.all([usersQuery.refetch(), adminRolesQuery.refetch()]);
    } catch (error) {
      console.error("Erro ao atualizar role admin:", error);
      toast.error("Não foi possível atualizar as permissões");
    } finally {
      setUpdatingUserId(null);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <PageHeaderCard
          eyebrow="Administração"
          title="Usuários do Sistema"
          subtitle="Gerencie acessos administrativos e vínculos dos usuários."
          backTo="/admin/control-center"
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            placeholder: "Buscar por nome ou email...",
          }}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="rounded-2xl border-border/60 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                  <Users className="h-4 w-4 text-violet-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total de Usuários</p>
                  <p className="text-lg font-semibold tabular-nums text-foreground">{totals.totalUsers}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/60 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                  <Shield className="h-4 w-4 text-violet-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Administradores</p>
                  <p className="text-lg font-semibold tabular-nums text-foreground">{totals.totalAdmins}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/60 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                  <UserX className="h-4 w-4 text-violet-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Sem Empresa</p>
                  <p className="text-lg font-semibold tabular-nums text-foreground">{totals.semEmpresa}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="py-10 text-center text-muted-foreground">Carregando usuários...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Perfil</TableHead>
                    <TableHead>Admin</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {users.map((user) => {
                    const isAdminUser = adminIds.has(user.id);
                    const isUpdating = updatingUserId === user.id;

                    return (
                      <TableRow key={user.id}>
                        <TableCell>{user.name || "Não informado"}</TableCell>
                        <TableCell>{user.email || "Não informado"}</TableCell>
                        <TableCell>
                          {user.empresas?.nome_fantasia || user.empresas?.razao_social || "Sem vínculo"}
                        </TableCell>
                        <TableCell>{user.nivel ?? "-"}</TableCell>
                        <TableCell>
                          <Badge className={isAdminUser ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"}>
                            {isAdminUser ? "Administrador" : "Usuário"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {isAdminUser ? (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={isUpdating}
                              onClick={() => toggleAdminRole(user, false)}
                              className="gap-2"
                            >
                              <ShieldOff size={14} />
                              Remover Admin
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={isUpdating}
                              onClick={() => toggleAdminRole(user, true)}
                              className="gap-2"
                            >
                              <Shield size={14} />
                              Tornar Admin
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
